/**
 * Homepage signal selection + templated, deterministic prose.
 *
 * Two jobs, both pure (no network, no LLM, no React):
 *
 *  1. `selectHomeSignals` — score every featured signal across ALL five windows
 *     (the homepage already fetches 10/20/65/130/260 and used to throw four of
 *     them away) and pick a diversified best-3.
 *  2. `summarizeSignal` — a 2-3 sentence, past-tense description of what THIS
 *     signal's own analogs did. Descriptive history only: no forecast verbs, no
 *     aggregate accuracy / hit-rate framing.
 *
 * THE `forward_curve` LANDMINE (do not "simplify" this away):
 * `forward_curve.curve[h].returns` is SORTED ASCENDING by the backend
 * (`_summarize_returns` in query_utils.py), so it is NOT index-aligned to
 * `matches`. Pairing `matches[i]` with `returns[i]` silently produces
 * plausible-looking WRONG ticker/return pairs. Per-analog realized moves are
 * therefore recomputed here from each match's own `match_ohlcv`, using the same
 * index math the backend uses in `forward_curve_dist`:
 *
 *     close_start = match_ohlcv[bars - 1][4]
 *     close_h     = match_ohlcv[bars - 1 + h][4]
 *
 * where `bars` is the RAW slice window (`query.window` — already step-downsampled,
 * so 65 for the 6-month and 1-year presets, NOT 130/260) and OHLCV row index 4 is
 * the close (same index `ViewFeatured.tsx` reads). `match_ohlcv` is truncated
 * server-side to `bars + forecast_window` rows, so only `h <= forecast_window`
 * resolves; analogs whose rows are missing or short are skipped and excluded from
 * the reported count.
 */

import { WINDOW_LABELS, WINDOW_LABELS_LONG } from "./windows";
import { baseSymbol, displaySymbol } from "./delisted";

/** Close column of an OHLCV row: [date, open, high, low, close, volume]. */
const CLOSE_IDX = 4;

/** Placeholder the API substitutes for tickers past the anonymous tease cut
 * (`_HIDDEN_TICKER` in resources/feature.py). Such a row has no renderable name. */
const HIDDEN_MARKER = "?";

/** Signals with fewer analogs than this are skipped while better ones exist —
 * a handful of analogs is a thin story to lead the homepage with. Relaxed
 * automatically (see `selectHomeSignals`) on a quiet day. */
export const PREFERRED_MIN_ANALOGS = 12;

/** The backend's own `min_matches` gate; nothing below this is ever published. */
export const HARD_MIN_ANALOGS = 10;

/** Above this share of delisted analogs (0-100 scale, like `fraction_delisted`)
 * the card reads badly when a visitor drills in — mostly dead shells. */
export const MAX_FRACTION_DELISTED = 50;

/**
 * Per-window DTW similarity floor (`min_score` in daily.py's SLICE_PARAMS).
 * Match scores are only comparable WITHIN a window — a 0.86 mean is excellent at
 * 1-year and impossible at 2-week — so match quality is reported as headroom
 * above the window's own floor. Display-only: if the backend floors ever drift,
 * the quality label goes slightly stale, nothing breaks.
 */
const MIN_SCORE_BY_WINDOW: Record<number, number> = {
  10: 0.99,
  20: 0.93,
  65: 0.8,
  130: 0.8,
  260: 0.8,
};

/** Analogs averaged into the match-quality reading (the ones a visitor actually sees). */
const QUALITY_SAMPLE = 5;

export type SignalDirection = "up" | "down";

export type MatchQualityLevel = "strong" | "moderate" | "weak";

export interface MatchQuality {
  level: MatchQualityLevel;
  /** Short chip label, e.g. "Close match". */
  label: string;
  /** Mean similarity of the top analogs, 0-1. */
  meanScore: number;
  /** Position of `meanScore` between this window's floor and a perfect 1.0, 0-1. */
  headroom: number;
}

export interface AnalogOutcome {
  /** Raw symbol — the identity key; may carry a "-DELISTED" suffix. */
  symbol: string;
  /** Display form: suffix stripped, "*" appended when delisted. */
  label: string;
  /** Company name, or "" when the payload only echoed the symbol back. */
  name: string;
  delisted: boolean;
  /** DTW similarity, 0-1 — higher is closer. */
  score: number;
  /** Realized move over the horizon, in percent. */
  returnPct: number;
  /** Last date of the analog's own historical window (ISO), or "". */
  asOf: string;
}

export interface SignalSummary {
  /** The three sentences joined with spaces — what the card renders. */
  text: string;
  sentences: string[];
  /** Analogs that actually resolved at this horizon (the reported denominator). */
  resolved: number;
  /** Of those, how many moved in the signal's called direction. */
  agreed: number;
  /** Median realized move across all resolved analogs, in percent. */
  medianReturn: number;
  /** Horizon used, in trading sessions. */
  horizon: number;
  closest: AnalogOutcome | null;
}

export interface HomeSignal extends StockData {
  direction: SignalDirection;
  /** DISPLAY window (10/20/65/130/260) — what `/view-featured` and Featured.tsx key on.
   * Not the raw slice window; read `query.window` for OHLCV index math. */
  window: number;
  /** Display windows (this one included) carrying the same ticker in the same
   * direction. Length >= 2 means cross-window confluence. */
  confluenceWindows: number[];
  compositeScore: number;
  quality: MatchQuality | null;
}

/* ------------------------------------------------------------------ helpers */

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function isRenderableTicker(ticker: string | undefined): boolean {
  return !!ticker && !ticker.includes(HIDDEN_MARKER);
}

/**
 * `count_total` is set on every featured entry by `add_matches_score` (daily.py)
 * but is not declared on the shared `StockData` type; read it defensively and
 * fall back to the match list length.
 */
export function analogCount(stock: StockData): number {
  const raw = (stock as unknown as { count_total?: number }).count_total;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  return stock.matches.length;
}

function numberAt(row: unknown, index: number): number | null {
  if (!Array.isArray(row)) return null;
  const value: unknown = row[index];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** A usable company name, or "" — never the "-DELISTED" symbol echoed back as a name. */
function cleanAnalogName(name: string | undefined, symbol: string): string {
  if (!name) return "";
  const trimmed = name.trim();
  if (!trimmed || !isRenderableTicker(trimmed)) return "";
  // The backend falls back to the symbol when it has no resolved name, which for a
  // delisted analog yields e.g. "BAC.C-DELISTED" — never render that as a company.
  if (/-delisted$/i.test(trimmed)) return "";
  if (trimmed.toUpperCase() === symbol.toUpperCase()) return "";
  if (trimmed.toUpperCase() === baseSymbol(symbol).toUpperCase()) return "";
  return trimmed;
}

function median(sorted: number[]): number {
  const n = sorted.length;
  if (n === 0) return 0;
  return n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
}

function signedPct(value: number): string {
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function monthYear(iso: string): string {
  if (!iso) return "";
  const parsed = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Window names for running prose ("matched ALRS's last year"), where
 * WINDOW_LABELS_LONG's "1 year" / "2 weeks" read as stray digits mid-sentence.
 */
const WINDOW_PROSE: Record<number, string> = {
  10: "two weeks",
  20: "four weeks",
  65: "three months",
  130: "six months",
  260: "year",
};

function proseWindowLabel(displayWindow: number): string {
  if (displayWindow in WINDOW_PROSE) return WINDOW_PROSE[displayWindow];
  if (displayWindow in WINDOW_LABELS_LONG) return WINDOW_LABELS_LONG[displayWindow];
  return `${displayWindow} sessions`;
}

/** Plain-English horizon, in trading sessions (5/10/20 land exactly on week/fortnight/month). */
export function horizonPhrase(sessions: number): string {
  if (sessions <= 1) return "the next session";
  if (sessions === 5) return "the following week";
  if (sessions === 10) return "the following two weeks";
  if (sessions === 20) return "the following month";
  return `the next ${sessions} sessions`;
}

/* --------------------------------------------------------- analog outcomes */

/**
 * Realized move of each analog at `horizon` sessions past the end of its matched
 * window, recomputed per match from `match_ohlcv`. See the module header for why
 * `forward_curve.returns` must NOT be used for this.
 *
 * @param horizon trading sessions forward; defaults to (and is capped at) the
 *   signal's committed `forecast_window`, past which `match_ohlcv` has no rows.
 */
export function analogOutcomes(stock: StockData, horizon?: number): AnalogOutcome[] {
  const bars = stock.query.window;
  const forecast = stock.query.forecast_window;
  if (!Number.isFinite(bars) || bars < 1 || !Number.isFinite(forecast) || forecast < 1) return [];

  const h = Math.min(Math.max(1, Math.trunc(horizon ?? forecast)), forecast);
  const startIdx = bars - 1;
  const endIdx = startIdx + h;

  const outcomes: AnalogOutcome[] = [];
  for (const match of stock.matches) {
    const rows = match.match_ohlcv;
    if (!Array.isArray(rows) || rows.length <= endIdx) continue;

    const closeStart = numberAt(rows[startIdx], CLOSE_IDX);
    const closeEnd = numberAt(rows[endIdx], CLOSE_IDX);
    if (closeStart === null || closeEnd === null || closeStart <= 0) continue;

    const symbol = match.match_symbol;
    if (!isRenderableTicker(symbol)) continue;

    // `delisted` is absent from payloads cached before the flag shipped — fall back
    // to the suffix heuristic that `displaySymbol` already implements.
    const delisted = (match as { delisted?: boolean }).delisted;
    const dates = match.match_dates;
    const asOf = Array.isArray(dates) && dates.length > startIdx ? dates[startIdx] : "";

    outcomes.push({
      symbol,
      label: displaySymbol(symbol, delisted),
      name: cleanAnalogName(match.match_symbol_name, symbol),
      delisted: delisted ?? /-delisted$/i.test(symbol),
      score: Number.isFinite(match.match_score) ? match.match_score : 0,
      returnPct: (closeEnd / closeStart - 1) * 100,
      asOf: typeof asOf === "string" ? asOf : "",
    });
  }
  return outcomes;
}

/* ----------------------------------------------------------- match quality */

/**
 * How close this signal's analogs actually are, expressed as headroom above the
 * window's own similarity floor. Deliberately never suppresses a signal — the
 * product decision is to show every ticker and let a weak row READ as weak.
 */
export function rateMatchQuality(stock: StockData, displayWindow: number): MatchQuality | null {
  const scores = stock.matches
    .map((m) => m.match_score)
    .filter((s) => typeof s === "number" && Number.isFinite(s))
    .sort((a, b) => b - a)
    .slice(0, QUALITY_SAMPLE);
  if (scores.length === 0) return null;

  const meanScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const floor = MIN_SCORE_BY_WINDOW[displayWindow] ?? Math.min(...scores);
  const span = Math.max(1e-6, 1 - floor);
  const headroom = clamp01((meanScore - floor) / span);

  const level: MatchQualityLevel =
    headroom >= 0.45 ? "strong" : headroom >= 0.25 ? "moderate" : "weak";
  const label =
    level === "strong" ? "Very close match" : level === "moderate" ? "Close match" : "Loose match";

  return { level, label, meanScore, headroom };
}

/* ------------------------------------------------------------ the sentences */

/**
 * 2-3 past-tense sentences describing what this signal's own analogs did.
 * Returns null when nothing resolves (no analogs, or `match_ohlcv` too short).
 */
export function summarizeSignal(
  stock: StockData,
  direction: SignalDirection,
  displayWindow: number,
): SignalSummary | null {
  const outcomes = analogOutcomes(stock);
  if (outcomes.length === 0) return null;

  const horizon = Math.max(1, Math.trunc(stock.query.forecast_window));
  const returns = outcomes.map((o) => o.returnPct).sort((a, b) => a - b);
  const resolved = outcomes.length;
  const agreed = outcomes.filter((o) =>
    direction === "up" ? o.returnPct > 0 : o.returnPct < 0,
  ).length;
  const med = median(returns);

  const ticker = baseSymbol(stock.query.ticker);
  const windowLabel = proseWindowLabel(displayWindow);
  const phrase = horizonPhrase(horizon);
  const verb = direction === "up" ? "rose" : "fell";

  const sentences: string[] = [
    `${resolved} historical ${resolved === 1 ? "window" : "windows"} matched ${ticker}'s last ${windowLabel}.`,
    `${agreed} of them ${verb} over ${phrase}; the median move across all ${resolved} was ${signedPct(med)}.`,
  ];

  const closest = outcomes.reduce<AnalogOutcome | null>(
    (best, o) => (best === null || o.score > best.score ? o : best),
    null,
  );
  if (closest) {
    const when = monthYear(closest.asOf);
    // Bare ticker here, not `closest.label` — this sentence spells out delisted status
    // in plain words below rather than via the chart legend's "*" convention, so the
    // asterisk (which needs the footnote to decode) never appears inline in prose.
    const ticker = baseSymbol(closest.symbol);
    const who = closest.name ? `${closest.name} (${ticker})` : ticker;
    const outcomeSentence = when
      ? `The closest analog was ${who} in ${when}, which moved ${signedPct(closest.returnPct)} over the same stretch.`
      : `The closest analog was ${who}, which moved ${signedPct(closest.returnPct)} over the same stretch.`;
    sentences.push(
      closest.delisted ? `${outcomeSentence} ${ticker} has since been delisted.` : outcomeSentence,
    );
  }

  return { text: sentences.join(" "), sentences, resolved, agreed, medianReturn: med, horizon, closest };
}

/* ------------------------------------------------------------- composite pick */

interface Candidate {
  stock: StockData;
  ticker: string;
  direction: SignalDirection;
  window: number;
  base: number;
  quality: MatchQuality | null;
}

type FeaturedKey = "featured10" | "featured20" | "featured65" | "featured130" | "featured260";

const WINDOW_SOURCES: ReadonlyArray<[FeaturedKey, number]> = [
  ["featured10", 10],
  ["featured20", 20],
  ["featured65", 65],
  ["featured130", 130],
  ["featured260", 260],
];

/** Composite of conviction, agreement ratio, analog depth, move amplitude and
 * match quality — each squashed to 0-1 so no single term can dominate. */
function scoreCandidate(
  stock: StockData,
  direction: SignalDirection,
  quality: MatchQuality | null,
): number {
  const conviction = direction === "up" ? stock.fraction_up : stock.fraction_dn;
  const ratio = direction === "up" ? stock.up_over_down : stock.down_over_up;
  const move = Math.abs(direction === "up" ? stock.avg_up : stock.avg_dn);
  const count = analogCount(stock);
  const forecast = Math.max(1, stock.query.forecast_window);

  // 50% agreement is a coin flip; featured selection already requires >60.
  const convictionScore = clamp01((conviction - 50) / 40);
  // up_over_down / down_over_up are clipped to 5 server-side.
  const ratioScore = clamp01(ratio / 5);
  // Log depth: 90 analogs is stronger evidence than 12, but not 7.5x stronger.
  const depthScore = clamp01(Math.log(count / HARD_MIN_ANALOGS) / Math.log(6));
  // A card where nothing happened is a boring card. Normalized by sqrt(horizon)
  // so 1-year signals (which move ~5x further) don't automatically outrank 2-week ones.
  const amplitudeScore = clamp01(move / (2 * Math.sqrt(forecast)));
  const qualityScore = quality ? quality.headroom : 0;

  return (
    0.28 * convictionScore +
    0.14 * ratioScore +
    0.2 * depthScore +
    0.2 * amplitudeScore +
    0.18 * qualityScore
  );
}

function collectCandidates(content: RootStockData, minAnalogs: number): Candidate[] {
  const candidates: Candidate[] = [];
  for (const [key, displayWindow] of WINDOW_SOURCES) {
    const featured = content[key];
    if (!featured) continue;
    for (const direction of ["up", "down"] as const) {
      // A 200 with a partial body can omit these, so widen before guarding.
      const rows = featured[direction] as StockData[] | undefined;
      if (!rows || rows.length === 0) continue;
      for (const stock of rows) {
        const query = stock.query as Query | undefined;
        const matches = stock.matches as Match[] | undefined;
        if (!query || !matches || matches.length === 0) continue;
        // Anonymous visitors get "?????" past the top few per window — those rows
        // have no renderable ticker, name or analog symbols.
        if (!isRenderableTicker(query.ticker)) continue;
        if (analogCount(stock) < minAnalogs) continue;
        const delistedShare = Number.isFinite(stock.fraction_delisted) ? stock.fraction_delisted : 0;
        if (delistedShare > MAX_FRACTION_DELISTED) continue;

        const quality = rateMatchQuality(stock, displayWindow);
        candidates.push({
          stock,
          ticker: baseSymbol(query.ticker),
          direction,
          window: displayWindow,
          base: scoreCandidate(stock, direction, quality),
          quality,
        });
      }
    }
  }
  return candidates;
}

/** Same ticker, same direction, 2+ windows. Rare (~3 of ~93 featured tickers on a
 * real day), so it is a BONUS — never a requirement. */
const CONFLUENCE_BONUS = 0.12;
const MAX_CONFLUENCE_BONUS = 0.25;

/** Discourage (never forbid) a third card from an already-used window / a
 * single-direction sweep, so the trio reads as a cross-section. */
const WINDOW_REPEAT_PENALTY = 0.1;
const WINDOW_THIRD_PENALTY = 0.25;
const ONE_DIRECTION_PENALTY = 0.3;

/**
 * Best `limit` signals across every window, at most one card per ticker,
 * preferring both directions and a spread of windows.
 *
 * Degrades gracefully: the analog-count / delisted filters are relaxed (and then
 * dropped) if they'd leave fewer than `limit` candidates, and an empty or short
 * payload just yields fewer cards — the caller keeps its "no samples" fallback.
 */
export function selectHomeSignals(
  content: RootStockData | undefined,
  limit = 3,
): HomeSignal[] {
  if (!content) return [];

  let candidates = collectCandidates(content, PREFERRED_MIN_ANALOGS);
  if (candidates.length < limit) {
    const relaxed = collectCandidates(content, HARD_MIN_ANALOGS);
    if (relaxed.length > candidates.length) candidates = relaxed;
  }
  if (candidates.length < limit) {
    const bare = collectCandidates(content, 1);
    if (bare.length > candidates.length) candidates = bare;
  }
  if (candidates.length === 0) return [];

  const confluence = new Map<string, Set<number>>();
  for (const c of candidates) {
    const key = `${c.ticker}|${c.direction}`;
    const windows = confluence.get(key) ?? new Set<number>();
    windows.add(c.window);
    confluence.set(key, windows);
  }

  const totals = new Map<Candidate, number>();
  for (const c of candidates) {
    const windows = confluence.get(`${c.ticker}|${c.direction}`);
    const extra = Math.max(0, (windows?.size ?? 1) - 1);
    totals.set(c, c.base + Math.min(MAX_CONFLUENCE_BONUS, CONFLUENCE_BONUS * extra));
  }

  // Deterministic ordering before the greedy pass so equal scores never flip
  // between renders.
  const remaining = [...candidates].sort((a, b) => {
    const diff = (totals.get(b) ?? 0) - (totals.get(a) ?? 0);
    return diff !== 0 ? diff : a.ticker.localeCompare(b.ticker) || a.window - b.window;
  });

  const picked: Candidate[] = [];
  while (picked.length < limit && remaining.length > 0) {
    let bestIdx = 0;
    let bestValue = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const c = remaining[i];
      const sameWindow = picked.filter((p) => p.window === c.window).length;
      const allSameDirection =
        picked.length === limit - 1 && picked.every((p) => p.direction === c.direction);
      let penalty = 0;
      if (sameWindow >= 1) penalty += WINDOW_REPEAT_PENALTY;
      if (sameWindow >= 2) penalty += WINDOW_THIRD_PENALTY;
      if (allSameDirection) penalty += ONE_DIRECTION_PENALTY;
      const value = (totals.get(c) ?? 0) - penalty;
      if (value > bestValue) {
        bestValue = value;
        bestIdx = i;
      }
    }
    const chosen = remaining[bestIdx];
    picked.push(chosen);
    // One card per ticker, in either direction.
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (remaining[i].ticker === chosen.ticker) remaining.splice(i, 1);
    }
  }

  return picked.map((c) => ({
    ...c.stock,
    direction: c.direction,
    window: c.window,
    confluenceWindows: [...(confluence.get(`${c.ticker}|${c.direction}`) ?? [c.window])].sort(
      (a, b) => a - b,
    ),
    compositeScore: totals.get(c) ?? c.base,
    quality: c.quality,
  }));
}

/** "3 timeframes agree" badge text, or "" when this ticker only fired in one window. */
export function confluenceLabel(windows: number[]): string {
  if (windows.length < 2) return "";
  return `${windows.length} timeframes agree`;
}

/** Title-case window name for the card badge, e.g. "4-Week". */
export function windowBadgeLabel(displayWindow: number): string {
  return WINDOW_LABELS[String(displayWindow)] ?? `${displayWindow}d`;
}
